import { clearAccessToken, getAccessToken, setAccessToken } from '@/lib/auth-token';
import { ApiClientError, errorFromResponse } from '@/lib/api-error';
import { redirectToWebLogin } from '@/lib/redirect-to-login';
import { getApiUrl } from '@/lib/urls';

export type ApiRequestOptions = {
  method?: string;
  body?: unknown;
  headers?: HeadersInit;
  auth?: boolean;
  signal?: AbortSignal;
};

type RefreshListener = (token: string | null) => void;

let refreshPromise: Promise<string | null> | null = null;
const refreshListeners = new Set<RefreshListener>();

function notifyRefreshListeners(token: string | null): void {
  for (const listener of refreshListeners) {
    listener(token);
  }
}

export function onAccessTokenChange(listener: RefreshListener): () => void {
  refreshListeners.add(listener);
  return () => refreshListeners.delete(listener);
}

async function refreshAccessToken(): Promise<string | null> {
  const response = await fetch(`${getApiUrl()}/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    clearAccessToken();
    notifyRefreshListeners(null);
    return null;
  }

  const data = (await response.json()) as { accessToken: string };
  setAccessToken(data.accessToken);
  notifyRefreshListeners(data.accessToken);
  return data.accessToken;
}

export async function ensureAccessToken(): Promise<string | null> {
  const existing = getAccessToken();
  if (existing) {
    return existing;
  }
  return refreshAccessTokenOnce();
}

async function refreshAccessTokenOnce(): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = refreshAccessToken().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

export async function apiRequest<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  const { method = 'GET', body, headers, auth = true, signal } = options;
  const url = path.startsWith('http') ? path : `${getApiUrl()}${path.startsWith('/') ? path : `/${path}`}`;

  const requestHeaders = new Headers(headers);
  requestHeaders.set('Accept', 'application/json');
  if (body !== undefined) {
    requestHeaders.set('Content-Type', 'application/json');
  }

  if (auth) {
    const token = await ensureAccessToken();
    if (!token) {
      throw new ApiClientError(401, 'UNAUTHORIZED', 'Authentication required');
    }
    requestHeaders.set('Authorization', `Bearer ${token}`);
  }

  const execute = (accessToken?: string) => {
    const finalHeaders = new Headers(requestHeaders);
    if (accessToken) {
      finalHeaders.set('Authorization', `Bearer ${accessToken}`);
    }

    return fetch(url, {
      method,
      credentials: auth ? 'include' : 'same-origin',
      headers: finalHeaders,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal,
    });
  };

  let response = await execute(auth ? getAccessToken() ?? undefined : undefined);

  if (auth && response.status === 401) {
    const hadToken = Boolean(getAccessToken());
    const refreshed = await refreshAccessTokenOnce();
    if (refreshed) {
      response = await execute(refreshed);
    } else if (hadToken) {
      redirectToWebLogin();
    }
  }

  if (!response.ok) {
    throw await errorFromResponse(response);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

export type ApiDownloadResult = {
  blob: Blob;
  filename: string;
};

function parseContentDispositionFilename(header: string | null): string | null {
  if (!header) {
    return null;
  }

  const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const quotedMatch = header.match(/filename="([^"]+)"/i);
  if (quotedMatch?.[1]) {
    return quotedMatch[1];
  }

  const plainMatch = header.match(/filename=([^;]+)/i);
  if (plainMatch?.[1]) {
    return plainMatch[1].trim();
  }

  return null;
}

export async function apiDownload(path: string, options: Omit<ApiRequestOptions, 'body'> = {}): Promise<ApiDownloadResult> {
  const { method = 'GET', headers, auth = true, signal } = options;
  const url = path.startsWith('http') ? path : `${getApiUrl()}${path.startsWith('/') ? path : `/${path}`}`;

  const requestHeaders = new Headers(headers);
  if (auth) {
    const token = await ensureAccessToken();
    if (!token) {
      throw new ApiClientError(401, 'UNAUTHORIZED', 'Authentication required');
    }
    requestHeaders.set('Authorization', `Bearer ${token}`);
  }

  const execute = (accessToken?: string) => {
    const finalHeaders = new Headers(requestHeaders);
    if (accessToken) {
      finalHeaders.set('Authorization', `Bearer ${accessToken}`);
    }

    return fetch(url, {
      method,
      credentials: auth ? 'include' : 'same-origin',
      headers: finalHeaders,
      signal,
    });
  };

  let response = await execute(auth ? getAccessToken() ?? undefined : undefined);

  if (auth && response.status === 401) {
    const hadToken = Boolean(getAccessToken());
    const refreshed = await refreshAccessTokenOnce();
    if (refreshed) {
      response = await execute(refreshed);
    } else if (hadToken) {
      redirectToWebLogin();
    }
  }

  if (!response.ok) {
    throw await errorFromResponse(response);
  }

  const blob = await response.blob();
  const filename = parseContentDispositionFilename(response.headers.get('Content-Disposition')) ?? 'download';

  return { blob, filename };
}

export async function apiUpload<T>(
  path: string,
  file: File,
  fieldName = 'file',
  options: Omit<ApiRequestOptions, 'body'> = {},
): Promise<T> {
  const { method = 'POST', headers, auth = true, signal } = options;
  const url = path.startsWith('http') ? path : `${getApiUrl()}${path.startsWith('/') ? path : `/${path}`}`;

  const formData = new FormData();
  formData.append(fieldName, file);

  const requestHeaders = new Headers(headers);
  if (auth) {
    const token = await ensureAccessToken();
    if (!token) {
      throw new ApiClientError(401, 'UNAUTHORIZED', 'Authentication required');
    }
    requestHeaders.set('Authorization', `Bearer ${token}`);
  }

  const execute = (accessToken?: string) => {
    const finalHeaders = new Headers(requestHeaders);
    if (accessToken) {
      finalHeaders.set('Authorization', `Bearer ${accessToken}`);
    }

    return fetch(url, {
      method,
      credentials: auth ? 'include' : 'same-origin',
      headers: finalHeaders,
      body: formData,
      signal,
    });
  };

  let response = await execute(auth ? getAccessToken() ?? undefined : undefined);

  if (auth && response.status === 401) {
    const hadToken = Boolean(getAccessToken());
    const refreshed = await refreshAccessTokenOnce();
    if (refreshed) {
      response = await execute(refreshed);
    } else if (hadToken) {
      redirectToWebLogin();
    }
  }

  if (!response.ok) {
    throw await errorFromResponse(response);
  }

  return (await response.json()) as T;
}

export async function logout(): Promise<void> {
  try {
    await apiRequest<void>('/auth/logout', { method: 'POST' });
  } finally {
    clearAccessToken();
    notifyRefreshListeners(null);
  }
}
